import { useState } from 'react';
import { db, functions } from '../../config/firebase';
import { ref, set, update } from 'firebase/database';
import {
  getAuth,
  createUserWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../../contexts/AuthContext';

const UserForm = ({
  user,
  onClose,
  onSuccess,
}) => {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    firstName: user?.personalDetails?.firstName || '',
    lastName: user?.personalDetails?.lastName || '',
    email: user?.personalDetails?.email || '',
    role: user?.role || 'student',
    status: user?.status || 'active',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generatePassword = () => {
    const length = 12;
    const charset =
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setFormData((prev) => ({ ...prev, password }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const auth = getAuth();
      let userId = user?.id;

      // Ensure we're authenticated
      if (!auth.currentUser) {
        throw new Error('You must be logged in to perform this action');
      }

      if (!user) {
        if (formData.password.length < 8) {
          throw new Error('Password must be at least 8 characters long');
        }

        const userCredential = await createUserWithEmailAndPassword(
          auth,
          formData.email,
          formData.password,
        );
        userId = userCredential.user.uid;

        await updateProfile(userCredential.user, {
          displayName: `${formData.firstName} ${formData.lastName}`,
        });

        const userData = {
          uid: userId,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          status: formData.status,
          role: formData.role,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          personalDetails: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
          },
        };

        const collection =
          formData.role === 'admin' || formData.role === 'manager'
            ? 'users'
            : ['instructor', 'demonstrator'].includes(formData.role)
            ? 'instructors'
            : 'students';

        await set(ref(db, `${collection}/${userId}`), userData);

        try {
          const sendWelcomeEmail = httpsCallable(functions, 'sendWelcomeEmail');
          await sendWelcomeEmail({
            email: formData.email,
            name: `${formData.firstName} ${formData.lastName}`,
            role: formData.role,
          });
        } catch (emailError) {
          console.error('Error sending welcome email:', emailError);
        }
      } else {
        const collection =
          formData.role === 'admin' || formData.role === 'manager'
            ? 'users'
            : ['instructor', 'demonstrator'].includes(formData.role)
            ? 'instructors'
            : 'students';

        const updates = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          status: formData.status,
          role: formData.role,
          updatedAt: new Date().toISOString(),
          personalDetails: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
          },
        };

        if (formData.password) {
          try {
            // Prompt for admin password
            const adminPassword = prompt(
              'Please enter your admin password to continue:',
            );
            if (!adminPassword) {
              throw new Error(
                'Admin password is required to update user passwords',
              );
            }

            const updateUserPassword = httpsCallable(
              functions,
              'updateUserPassword',
            );
            const result = await updateUserPassword({
              uid: userId,
              newPassword: formData.password,
              email: formData.email,
              adminUid: currentUser.uid,
              adminEmail: currentUser.email,
              adminPassword: adminPassword,
            });

            if (!result.data.success) {
              throw new Error('Failed to update password');
            }
          } catch (passwordError) {
            console.error('Error updating password:', passwordError);
            throw new Error(
              passwordError.code === 'permission-denied'
                ? 'You do not have permission to update passwords'
                : passwordError.message || 'Failed to update password',
            );
          }
        }

        await update(ref(db, `${collection}/${userId}`), updates);
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving user:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full">
      <h2 className="text-xl font-semibold mb-4">
        {user ? 'Edit User' : 'Add New User'}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              First Name
            </label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, firstName: e.target.value }))
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Last Name
            </label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, lastName: e.target.value }))
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Email
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, email: e.target.value }))
            }
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
            disabled={!!user}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Role
          </label>
          <select
            value={formData.role}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, role: e.target.value }))
            }
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="student">Student</option>
            <option value="instructor">Instructor</option>
            <option value="demonstrator">Demonstrator</option>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Status
          </label>
          <select
            value={formData.status}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, status: e.target.value }))
            }
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            {user ? 'New Password (optional)' : 'Password'}
          </label>
          <div className="mt-1 flex gap-2">
            <input
              type="text"
              value={formData.password}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, password: e.target.value }))
              }
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required={!user}
              minLength={8}
              placeholder={user ? 'Leave blank to keep current password' : ''}
            />
            <button
              type="button"
              onClick={generatePassword}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            >
              Generate
            </button>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {user
              ? 'Enter a new password only if you want to change it'
              : 'Password must be at least 8 characters long'}
          </p>
        </div>

        {error && <div className="text-red-500 text-sm">{error}</div>}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : user ? 'Update User' : 'Add User'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserForm;
